import type { BindingScope, ProviderField, ProviderFieldSchema } from "@tilt/admin-sdk";

import { fieldScope } from "@/lib/binding-scope";
import { validateEmail, validatePhone } from "@/lib/validation";

/** True when the schema has at least one property declared. Used to
 *  decide between schema-driven form and raw-JSON fallback. */
export function hasProperties(schema: ProviderFieldSchema | undefined | null): boolean {
  return !!schema?.properties && Object.keys(schema.properties).length > 0;
}

// HH:MM 24-hour check for time fields.
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
// Narrow URL check; strict URL validation is out of scope here.
const URL_RE = /^https?:\/\/\S+$/i;

/**
 * Build + validate a clean payload from a SchemaForm's values state.
 * Drops empty values when the field is not required; rejects missing
 * required fields, malformed numerics, bad formats (email/phone/url),
 * invalid times (HH:MM), and empty-array required fields. Also
 * produces an auto-masked summary suitable for the credentials flow.
 *
 * When a binding ``scope`` is supplied, fields whose ``x-scope``
 * doesn't match are skipped entirely - they're rendered (and required)
 * at a different scope level by the resolver, so validating them at
 * the wrong level would block the submit on values that aren't even
 * supposed to be on this row. Concrete example: Valor's ``merchant_id``
 * is ``x-scope: location``; a partner-scope Valor binding shouldn't
 * fail required-check on it because the location row will carry it.
 */
export function buildValuesFromSchema(
  schema: ProviderFieldSchema,
  values: Record<string, unknown>,
  options?: { scope?: BindingScope },
): {
  payload: Record<string, unknown>;
  masked: Record<string, unknown>;
  error?: string;
} {
  const payload: Record<string, unknown> = {};
  const masked: Record<string, unknown> = {};
  const entries: [string, ProviderField][] = Object.entries(schema.properties ?? {});
  const title = (k: string, f: ProviderField): string => f.title ?? k;
  const scope = options?.scope;

  for (const [key, field] of entries) {
    // Skip fields out of scope. Credential schemas don't carry
    // x-scope (they're flat per-credential), so fieldScope returns
    // the partner default and the comparison passes - credentials
    // path is unaffected.
    if (scope && fieldScope(field) !== scope) {
      continue;
    }
    const raw = values[key];
    const isEmpty =
      raw === undefined || raw === null || raw === "" || (Array.isArray(raw) && raw.length === 0);
    if (isEmpty) {
      if (field.required) {
        return { payload, masked, error: `${title(key, field)} is required` };
      }
      continue;
    }

    if (field.type === "boolean") {
      const v = Boolean(raw);
      payload[key] = v;
      if (!field.secret) masked[key] = v;
      continue;
    }

    if (field.type === "number") {
      const n = Number(raw);
      if (!Number.isFinite(n)) {
        return { payload, masked, error: `${title(key, field)}: must be a number` };
      }
      payload[key] = n;
      if (!field.secret) masked[key] = n;
      continue;
    }

    if (field.type === "time") {
      const s = String(raw);
      if (!TIME_RE.test(s)) {
        return { payload, masked, error: `${title(key, field)}: must be HH:MM (24-hour)` };
      }
      payload[key] = s;
      masked[key] = s;
      continue;
    }

    if (field.type === "enum") {
      payload[key] = String(raw);
      masked[key] = String(raw);
      continue;
    }

    if (field.type === "array") {
      if (!Array.isArray(raw)) {
        return { payload, masked, error: `${title(key, field)}: must be a list` };
      }
      const arr = raw.map((x) => String(x)).filter((x) => x !== "");
      payload[key] = arr;
      masked[key] = arr;
      continue;
    }

    // default: string - honor format.
    const s = String(raw);
    if (field.format === "email") {
      const check = validateEmail(s, { required: Boolean(field.required) });
      if (!check.ok) {
        return { payload, masked, error: `${title(key, field)}: ${check.error}` };
      }
    } else if (field.format === "phone") {
      const check = validatePhone(s, { required: Boolean(field.required) });
      if (!check.ok) {
        return { payload, masked, error: `${title(key, field)}: ${check.error}` };
      }
    } else if (field.format === "url" && !URL_RE.test(s)) {
      return { payload, masked, error: `${title(key, field)}: must be an http(s) URL` };
    }
    payload[key] = s;
    if (field.secret) {
      if (s.length >= 4) masked[`${key}_last4`] = s.slice(-4);
    } else {
      masked[key] = s;
    }
  }
  return { payload, masked };
}
